import moment from 'moment';

// Geometry and date math for the Weekly Update's Gantt strip.
//
// Kept out of the component for one reason: the strip has to line up row for
// row with the grid it sits beside, so the row height is a shared constant
// rather than a number each side picks, and the bar span has to follow the
// same rules the grid's "per day" column already shows — a row with no finish
// date but a daily rate is planned, and the picture should say so.

const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
};

/** Grid row height, shared by the table's <td> and the strip's tracks. */
export const ROW_H = 38;
/** Header height, shared by the table's <th> and the strip's date axis. */
export const HEAD_H = 30;

const worksOn = (m, workDays) => {
    const wd = m.day();
    if (workDays >= 7) return true;
    if (workDays === 5) return wd !== 0 && wd !== 6;
    return wd !== 0;                              // 6-day Mon–Sat, the default
};

/**
 * The date `workingDays` working days after `start`, counting `start` itself
 * as the first one. Inverse of workingDayCount(), so a bar inferred from a
 * daily rate ends where that rate actually runs out.
 */
export const finishAfter = (start, workingDays, workDays = 6) => {
    const d = moment(start, 'YYYY-MM-DD');
    if (!d.isValid()) return null;
    let left = Math.max(Math.ceil(workingDays), 1);
    if (worksOn(d, workDays)) left -= 1;
    let guard = 0;
    while (left > 0 && guard < 4000) {
        d.add(1, 'day');
        if (worksOn(d, workDays)) left -= 1;
        guard += 1;
    }
    return d.format('YYYY-MM-DD');
};

/**
 * A grid row → the span to draw, or null when there is nothing to draw yet.
 * `inferred` marks a finish the daily rate implied rather than one the PM
 * typed; `invalid` marks a finish before its start, which the grid already
 * flags in words.
 */
export const barOf = (r) => {
    if (!r) return null;
    const start = r.startDate && moment(r.startDate, 'YYYY-MM-DD').isValid() ? r.startDate : null;
    if (!start) return null;

    const typed = r.finishDate && moment(r.finishDate, 'YYYY-MM-DD').isValid() ? r.finishDate : null;
    if (typed) {
        return moment(typed).isBefore(moment(start))
            ? { start: typed, finish: start, inferred: false, invalid: true }
            : { start, finish: typed, inferred: false, invalid: false };
    }

    const rate = num(r.dailyRate);
    const qty = num(r.qty);
    if (!rate || !qty) return null;
    return {
        start,
        finish: finishAfter(start, qty / rate, r.workDays || 6),
        inferred: true,
        invalid: false,
    };
};

/**
 * The window every bar fits inside, padded out to whole months so the axis
 * labels have somewhere to sit. Today is pulled in when it falls near the
 * plan, so the marker stays useful on a job that has not started.
 */
export const ganttDomain = (rows, todayIso) => {
    const bars = (rows || []).map(barOf).filter(Boolean);
    if (bars.length === 0) return null;

    let start = bars[0].start;
    let end = bars[0].finish;
    bars.forEach((b) => {
        if (b.start < start) start = b.start;
        if (b.finish > end) end = b.finish;
    });

    const today = todayIso || moment().format('YYYY-MM-DD');
    const a = moment.min(moment(start), moment(today)).startOf('month');
    const b = moment.max(moment(end), moment(today)).endOf('month');
    // A job that finished two years ago should not squeeze the plan into a
    // sliver, so today only widens the window by up to a quarter either side.
    const lo = moment.max(a, moment(start).startOf('month').subtract(3, 'months'));
    const hi = moment.min(b, moment(end).endOf('month').add(3, 'months'));
    return {
        start: lo.format('YYYY-MM-DD'),
        end: hi.format('YYYY-MM-DD'),
        days: Math.max(hi.diff(lo, 'days') + 1, 1),
    };
};

/** Month boundaries inside the domain, for gridlines and axis labels. */
export const monthTicks = (domain) => {
    if (!domain) return [];
    const out = [];
    const end = moment(domain.end);
    for (const t = moment(domain.start).startOf('month'); t.isSameOrBefore(end); t.add(1, 'month')) {
        if (t.isBefore(moment(domain.start))) continue;
        out.push({
            key: t.format('YYYY-MM'),
            date: t.format('YYYY-MM-DD'),
            label: t.format('MMM'),
            year: t.format('YY'),
            jan: t.month() === 0,
        });
    }
    return out;
};

export default barOf;
